import React, { useMemo, useEffect, useState } from "react";
import "../Implementation.scss";
import { debounce } from "lodash";
import { Button, Spin, Switch, Tabs } from "antd";
import ExportButton from "../../../../Assets/svg/exportButton.svg";
import CalendarView from "./CalendarView/CalendarView";
import KanbanView from "./KanbanView/KanbanView";
import TableView from "./TableView/TableView";
import TrackingModal from "../../planning/VisitBoard/Modals/AddTrackingCardModal";
import DeletedModal from "./TableView/modals/DeletedModal";
import { httpService } from "../../../../services/httpService.service";
import MessagePopUp from "../../../components/Messages/MessagePopUp";
import ExportSelectedTable from "../../../components/ExportExcel/ExportSelectedExcel/exportSelectedTable";
import { useLocation } from "react-router-dom";

const initialFilters = {
  Search: "",
  ImplementingPartnerId: [],
  State: [],
  SiteVisitStatus: [],
  ClusterId: [],
  Month: [],
  Year: [],
  Governates: [],
  Cities: [],
  DataCheckIds: [],
  IgnorePagination: false,
};

function VisitBoard() {
  const { search: page } = useLocation();
  const waitingForTracking = page === "?WaitingForTracking";
  const trackingInProgress = page === "?trackingInprogress";
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDeletedModalVisible, setIsDeletedModalVisible] = useState(false);
  const [siteVisitCards, setSiteVisitCards] = useState([]);
  const [selectedSiteVisitIds, setSelectedSiteVisitIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ pageSize: 10, current: 1 });
  const projectID = localStorage.getItem("currentProjectID");
  const [currentView, setCurrentView] = useState("1");
  const [message, setMessage] = useState({
    trigger: false,
    type: "",
    icon: "",
    title: "",
    text: "",
  });
  const [filters, setFilters] = useState(initialFilters);
  const [isExportModalVisible, setIsExportModalVisible] = useState(false);

  const handleRowSelectionChange = (selectedIds) => {
    setSelectedSiteVisitIds(selectedIds);
  };

  const handleModalCancel = () => {
    setIsExportModalVisible(false);
  };

  const handleExportClick = () => {
    setIsExportModalVisible(true);
  };

  const handleOk = () => {
    setIsModalVisible(false);
    fetchSiteVisitCards();
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const showDeletedModal = () => {
    setIsDeletedModalVisible(true);
  };

  const onChange = (key) => {
    setCurrentView(key);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await fetchSiteVisitCards();
      setLoading(false);
    };

    fetchData();
  }, [currentView]);

  const fetchSiteVisitCards = async (
    customFilters = filters,
    customPagination = pagination
  ) => {
    setLoading(true);

    const params = {
      ProjectId: projectID,
      includeOnlyWithTracking: waitingForTracking ? false : true,
      pageNumber: customPagination.current,
      pageSize: customPagination.pageSize,
      Search: customFilters.Search,
      ImplementingPartnerId: customFilters.ImplementingPartnerId,
      State: customFilters.State,
      SiteVisitStatus: customFilters?.SiteVisitStatus?.length
        ? customFilters.SiteVisitStatus
        : waitingForTracking
        ? [2, 3]
        : [4, 5],
      ClusterId: customFilters.ClusterId,
        DataCheckIds: customFilters.DataCheckIds,
      Month: customFilters.Month,
      Year: customFilters.Year,
      Governates: customFilters.Governates,
      Cities: customFilters.Cities,
      IgnorePagination: currentView === "3",
    };

    const expandArrayParams = (params) => {
      const expanded = [];
      for (const [key, value] of Object.entries(params)) {
        if (Array.isArray(value)) {
          value.forEach((v) => expanded.push([key, v]));
        } else if (
          value !== undefined &&
          value !== null &&
          !(typeof value === "number" && isNaN(value))
        ) {
          expanded.push([key, value]);
        }
      }
      return expanded;
    };

    const queryString = new URLSearchParams(
      expandArrayParams(params)
    ).toString();

    await httpService.get(
      `/api/Site/getAllSiteVisits?${queryString}`,
      (response) => {
        const siteVisitCardsData = response?.data;

        setPagination((prev) => ({
          ...prev,
          total: response.metaData.totalItemCount,
        }));

        setSiteVisitCards(siteVisitCardsData.length ? siteVisitCardsData : []);
        setLoading(false);
      },
      () => setLoading(false)
    );
  };

  const debouncedHandler = useMemo(
    () =>
      debounce((newFilters, newPagination) => {
        fetchSiteVisitCards(newFilters, newPagination);
      }, 400),
    []
  );

  useEffect(() => {
    debouncedHandler(filters, pagination);
    return () => debouncedHandler.cancel();
  }, [filters, pagination.current, pagination.pageSize]);

  useEffect(() => {
    fetchSiteVisitCards(filters, pagination);
  }, [page, currentView]);

  const onPageChange = (pageNumber, pageSize) => {
    setPagination({ ...pagination, current: pageNumber, pageSize });
  };
  const handleFilterChange = (key, value) => {
    setFilters({
      ...filters,
      [key]: value,
    });
    setPagination({ ...pagination, current: 1 });
  };

  const items = [
    {
      key: "1",
      label: "Table View",
      children: (
        <TableView
          onOk={handleOk}
          tracked={waitingForTracking ? false : true}
          siteVisitCards={siteVisitCards}
          fetchSiteVisitCards={fetchSiteVisitCards}
          loading={loading}
          setLoading={setLoading}
          onPageChange={onPageChange}
          pagination={pagination}
          setPagination={setPagination}
          handleFilterChange={handleFilterChange}
          filters={filters}
          setFilters={setFilters}
          onRowSelectionChange={handleRowSelectionChange}
        />
      ),
    },
    {
      key: "2",
      label: "Kanban View",
      children: (
        <KanbanView
          siteVisitCards={siteVisitCards}
          setSiteVisitCards={setSiteVisitCards}
          fetchSiteVisitCards={fetchSiteVisitCards}
          loading={loading}
          setLoading={setLoading}
          onPageChange={onPageChange}
          pagination={pagination}
          setPagination={setPagination}
          handleFilterChange={handleFilterChange}
          filters={filters}
          setFilters={setFilters}
        />
      ),
    },
    {
      key: "3",
      label: "Calendar View",
      children: (
        <CalendarView
          siteVisitCards={siteVisitCards}
          fetchSiteVisitCards={fetchSiteVisitCards}
          loading={loading}
          setLoading={setLoading}
          onPageChange={onPageChange}
          pagination={pagination}
          setPagination={setPagination}
          handleFilterChange={handleFilterChange}
          filters={filters}
          setFilters={setFilters}
        />
      ),
    },
  ];

  return (
    <div className="tableViewcontainer visitBoardComponent">
      <div className="tableHeader">
        <MessagePopUp
          trigger={message.trigger}
          type={message.type}
          icon={message.icon}
          messageTitle={message.title}
          messageText={message.text}
        />
        <span className="tableViewHeaderText">Visits Board</span>
        <div className="tableViewHeaderButtons">
          <div
            className="d-inline d-lg-none d-md-none"
            style={{ marginBottom: 16 }}
          >
            <span style={{ marginRight: 8 }}>Show Tracked</span>
            <Switch
              checked={trackingInProgress}
              onChange={() => (page = "?trackingInprogress")}
            />
          </div>

          <Button
            className={`addButtonTPM ${currentView != "1" ? "d-none" : ""}`}
            onClick={handleExportClick}
          >
            <img src={ExportButton} alt="ExportButton" />
            Export Selected Site Visits
          </Button>
          <TrackingModal
            visible={isModalVisible}
            onOk={handleOk}
            onCancel={handleCancel}
          />
          <DeletedModal
            visible={isDeletedModalVisible}
            onOk={showDeletedModal}
            onCancel={() => {
              setIsDeletedModalVisible(false);
            }}
            fetchSiteVisitCards={fetchSiteVisitCards}
          />
        </div>
      </div>
      <div className="tabsHeader">
        <Tabs
          activeKey={currentView}
          items={items}
          onChange={(key) => {
            if (!loading) {
              onChange(key);
            }
          }}
          className="customTabs"
        />
      </div>
      <ExportSelectedTable
        visible={isExportModalVisible}
        onCancel={handleModalCancel}
        selectedSiteVisitIds={selectedSiteVisitIds}
      />
    </div>
  );
}

export default VisitBoard;
